<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-6 flex justify-between items-center">
        <h1 class="text-3xl font-bold text-gray-900">{{ title }}</h1>
        <button
          @click="openCreateModal"
          class="bg-indigo-600 text-white px-6 py-2.5 rounded-lg hover:bg-indigo-700 transition flex items-center space-x-2 shadow-sm"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Novo</span>
        </button>
      </div>

      <slot name="filters" :load-items="applyFilters"></slot>

      <div v-if="loading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>

      <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {{ error }}
      </div>

      <div v-else class="bg-white rounded-lg shadow-sm overflow-hidden">
        <!-- Mobile: cards (bonito e legível) -->
        <div class="sm:hidden">
          <div v-if="validItems.length === 0" class="px-6 py-12 text-center text-gray-500">
            Nenhum registro encontrado
          </div>

          <div v-else class="divide-y divide-gray-200">
            <div
              v-for="item in validItems"
              :key="item.id"
              :class="['p-4 transition', item.blocked ? 'bg-red-50' : 'bg-white']"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900 truncate">
                    {{ formatColumnValue(columns[0], item) }}
                  </p>
                  <p v-if="item.blocked" class="text-xs font-medium text-red-700 mt-1">
                    Bloqueado
                  </p>
                </div>

                <div class="flex items-center gap-2 flex-shrink-0">
                  <button
                    @click="editItem(item)"
                    class="text-indigo-600 hover:text-indigo-900 p-1"
                    title="Editar"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    @click="confirmDelete(item)"
                    class="text-red-600 hover:text-red-900 p-1"
                    title="Excluir"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              <div class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <template v-for="column in columns" :key="column.key">
                  <div class="text-gray-500 truncate">{{ column.label }}</div>
                  <div class="text-gray-900 font-medium truncate">
                    {{ formatColumnValue(column, item) }}
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop: tabela -->
        <div class="hidden sm:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-200">
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
                >
                  {{ column.label }}
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <template v-if="validItems.length === 0">
                <tr>
                  <td :colspan="columns.length + 1" class="px-6 py-12 text-center text-gray-500">
                    Nenhum registro encontrado
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr
                  v-for="item in validItems"
                  :key="item.id"
                  :class="['transition', item.blocked ? 'bg-red-50 hover:bg-red-50' : 'hover:bg-gray-50']"
                >
                  <td
                    v-for="column in columns"
                    :key="column.key"
                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                  >
                    <template v-if="column.format">
                      {{ column.format(item[column.key], item) }}
                    </template>
                    <template v-else>
                      {{ item[column.key] || '-' }}
                    </template>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      @click="editItem(item)"
                      class="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      @click="confirmDelete(item)"
                      class="text-red-600 hover:text-red-900"
                    >
                      <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        
        <div v-if="pagination.count > 0" class="px-6 py-4 border-t border-gray-200 bg-white">
          <div v-if="pagination.pages <= 1" class="flex items-center justify-center">
            <p class="text-sm text-gray-700">
              Total de
              <span class="font-medium">{{ pagination.count }}</span>
              {{ pagination.count === 1 ? 'registro' : 'registros' }}
            </p>
          </div>
          
          <div v-else class="flex items-center justify-between">
            <div class="flex-1 flex justify-between sm:hidden">
              <button
                @click="changePage(pagination.page - 1)"
                :disabled="!pagination.prev"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                @click="changePage(pagination.page + 1)"
                :disabled="!pagination.next"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div class="flex items-center space-x-4">
              <p class="text-sm text-gray-700">
                Mostrando
                <span class="font-medium">{{ pagination.from }}</span>
                a
                <span class="font-medium">{{ pagination.to }}</span>
                de
                <span class="font-medium">{{ pagination.count }}</span>
                resultados
              </p>
            </div>
            <div>
              <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  @click="changePage(pagination.page - 1)"
                  :disabled="!pagination.prev"
                  class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
                
                <template v-for="page in visiblePages" :key="page">
                  <button
                    v-if="page !== '...'"
                    @click="changePage(page)"
                    :class="[
                      'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                      page === pagination.page
                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    ]"
                  >
                    {{ page }}
                  </button>
                  <span
                    v-else
                    class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                  >
                    ...
                  </span>
                </template>

                <button
                  @click="changePage(pagination.page + 1)"
                  :disabled="!pagination.next"
                  class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isModalOpen || loadingItemDetails"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div v-if="loadingItemDetails" class="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
        <p class="text-gray-600">Carregando dados...</p>
      </div>
      
      <div v-else class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h2 class="text-xl font-semibold text-gray-900">
            {{ editingItem ? 'Editar' : 'Novo' }} {{ singularTitle }}
          </h2>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="openSaveConfirm" class="flex flex-col flex-1 min-h-0">
          <div class="px-6 py-4 overflow-y-auto flex-1">
            <div v-if="formErrors.general" class="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {{ formErrors.general }}
            </div>
            
            <slot name="form" :form="form" :errors="formErrors"></slot>
          </div>

          <div class="px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0 bg-gray-50">
            <button
              v-if="editingItem"
              type="button"
              @click="confirmDelete(editingItem)"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
            >
              Excluir
            </button>
            <div v-else></div>
            <div class="flex space-x-3">
              <button
                type="submit"
                :disabled="saving"
                class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
              >
                {{ saving ? 'Salvando...' : 'Salvar' }}
              </button>
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Sair
              </button>              
            </div>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="deleteConfirmItem"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeDeleteModal"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h3 class="flex items-center text-lg font-semibold text-gray-900 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z" clip-rule="evenodd" />
          </svg>
          Confirmação!
        </h3>
        
        <div v-if="!deleteError">
          <p class="text-gray-600 mb-6">
            Tem certeza que deseja excluir este registro?<br>Esta ação não pode ser desfeita.
          </p>
        </div>

        <div v-else class="mb-6">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
              </svg>
              <div class="flex-1">
                <h4 class="text-red-800 font-semibold mb-1">{{ deleteError }}</h4>
                <p class="text-red-700 text-sm mb-3">{{ deleteErrorMessage }}</p>
                <div class="bg-white rounded-md p-3 border border-red-100">
                  <p class="text-sm text-gray-700">
                    <strong class="text-red-700">💡 Solução rápida:</strong> Use o botão de lixeira para excluir as hospedagens diretamente, ou clique no nome para editá-las.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="relatedStays && relatedStays.length > 0" class="space-y-2">
            <p class="text-sm font-semibold text-gray-700 mb-2">
              {{ relatedStays[0]?.is_cleaning !== undefined ? 'Hospedagens' : 'Estadias' }} relacionadas ({{ relatedStays.length }}):
            </p>
            <div class="bg-gray-50 rounded-lg divide-y divide-gray-200">
              <div
                v-for="stay in relatedStays"
                :key="stay.id"
                class="p-3 group"
              >
                <div class="flex items-center justify-between gap-3">
                  <div 
                    class="flex-1 cursor-pointer hover:opacity-75 transition"
                    @click="goToStay(stay.id)"
                  >
                    <div class="flex items-center gap-2 mb-1">
                      <p class="text-sm font-medium text-gray-900">{{ stay.guest_name }}</p>
                      <span
                        v-if="stay.is_cleaning"
                        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                      >
                        Faxina
                      </span>
                    </div>
                    <p class="text-xs text-gray-600" v-if="stay.booking_reference">
                      Reserva: {{ stay.booking_reference }}
                    </p>
                    <p class="text-xs text-gray-500">
                      Check-in: {{ formatDate(stay.check_in_date) }} | Check-out: {{ formatDate(stay.check_out_date) }}
                    </p>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      @click="openDeleteRelatedStayConfirm(stay)"
                      :disabled="deletingStayId === stay.id"
                      class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                      title="Excluir hospedagem"
                    >
                      <svg 
                        v-if="deletingStayId !== stay.id"
                        class="w-4 h-4" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <svg 
                        v-else
                        class="w-4 h-4 animate-spin" 
                        fill="none" 
                        viewBox="0 0 24 24"
                      >
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </button>
                    <button
                      @click="goToStay(stay.id)"
                      class="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      title="Ver detalhes"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-3">
          <button
            v-if="!deleteError"
            @click="deleteItem"
            :disabled="deleting"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
          >
            {{ deleting ? 'Excluindo...' : 'Sim' }}
          </button>          
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            {{ deleteError ? 'Fechar' : 'Não' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="saveConfirmOpen"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeSaveConfirm"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-gray-900 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z" clip-rule="evenodd" />
          </svg>
          Confirmação!
        </h3>
        <p class="text-gray-600 mb-6">
          Deseja salvar este {{ singularTitle.toLowerCase() }}?
        </p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="saving"
            @click="confirmSave"
            class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {{ saving ? 'Salvando...' : 'Sim' }}
          </button>          
          <button
            type="button"
            @click="closeSaveConfirm"
            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Não
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<ConfirmationModal
  :isOpen="!!relatedStayToDelete"
  title="Excluir hospedagem"
  :message="`Tem certeza que deseja excluir a hospedagem de ${relatedStayToDelete?.guest_name || ''}?`"
  :details="relatedStayToDelete ? `Check-in: ${formatDate(relatedStayToDelete.check_in_date)}<br/>Check-out: ${formatDate(relatedStayToDelete.check_out_date)}` : ''"
  confirmLabel="Excluir"
  cancelLabel="Cancelar"
  confirmButtonColor="red"
  :loading="deletingStayId === (relatedStayToDelete && relatedStayToDelete.id)"
  @confirm="confirmDeleteRelatedStay"
  @cancel="cancelDeleteRelatedStay"
/>

<script>
import axios from 'axios'
import { navigateTo } from '../router.js'
import ConfirmationModal from './ConfirmationModal.vue'

export default {
  components: {
    ConfirmationModal
  },
  props: {
    title: {
      type: String,
      required: true
    },
    singularTitle: {
      type: String,
      required: true
    },
    apiEndpoint: {
      type: String,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    formFields: {
      type: Object,
      required: true
    },
    customUpdateEndpoint: {
      type: Function,
      required: false,
      default: null
    },
    /** Chamado após montar o form no novo/editar (ex.: clonar objetos aninhados). */
    postProcessForm: {
      type: Function,
      required: false,
      default: null
    },
    /** Chamado antes de montar o payload (ex.: sincronizar canvas → File no form). Retorne false para abortar o save. */
    beforeSave: {
      type: Function,
      required: false,
      default: null
    }
  },

  data() {
    return {
      items: [],
      loading: false,
      error: null,
      isModalOpen: false,
      editingItem: null,
      form: {},
      formErrors: {},
      saving: false,
      deleteConfirmItem: null,
      deleting: false,
      deleteError: null,
      deleteErrorMessage: null,
      relatedStays: [],
      deletingStayId: null,
      saveConfirmOpen: false,
      relatedStayToDelete: null,
      filterParams: {},
      loadingItemDetails: false,
      pagination: {
        page: 1,
        items: 20,
        count: 0,
        pages: 0,
        from: 0,
        to: 0
      }
    }
  },

  computed: {
    validItems() {
      if (!Array.isArray(this.items)) {
        return []
      }
      return this.items.filter(item => item && item.id)
    },

    visiblePages() {
      const current = this.pagination.page
      const total = this.pagination.pages
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }

      return rangeWithDots
    }
  },

  mounted() {
    this.loadItems()
  },

  methods: {
    async loadItems(params = {}) {
      this.loading = true
      this.error = null
      this.filterParams = params

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        
        const allParams = { ...params, page: this.pagination.page }
        const queryString = new URLSearchParams(allParams).toString()
        const url = queryString ? `${this.apiEndpoint}?${queryString}` : this.apiEndpoint
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${userToken}`, 'Tenant-Authorization': `Bearer ${tenantToken}` }
        })
        
        if (response.data.data && response.data.pagy) {
          this.items = Array.isArray(response.data.data) ? response.data.data : []
          this.pagination = response.data.pagy
        } else {
          this.items = Array.isArray(response.data) ? response.data : []
        }
      } catch (err) {
        this.error = err.response?.data?.error || 'Erro ao carregar os dados'
      } finally {
        this.loading = false
      }
    },

    changePage(page) {
      this.pagination.page = page
      this.loadItems(this.filterParams)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    applyFilters(params = {}) {
      this.pagination.page = 1
      this.loadItems(params)
    },

    openCreateModal() {
      this.editingItem = null
      this.form = { ...this.formFields }
      if (typeof this.postProcessForm === 'function') {
        this.postProcessForm(this.form)
      }
      this.formErrors = {}
      this.isModalOpen = true
    },

    formatColumnValue(column, item) {
      if (!column || !item) return '-'
      const value = item[column.key]
      if (column.format) return column.format(value, item)
      if (value === null || value === undefined || value === '') return '-'
      return value
    },

    async editItem(item) {
      this.loadingItemDetails = true
      this.formErrors = {}
      
      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const baseEndpoint = this.apiEndpoint.split('?')[0]
        
        const response = await axios.get(`${baseEndpoint}/${item.id}`, {
          headers: { Authorization: `Bearer ${userToken}`, 'Tenant-Authorization': `Bearer ${tenantToken}` }
        })
        
        this.editingItem = response.data
        this.form = { ...this.formFields, ...response.data }
        if (typeof this.postProcessForm === 'function') {
          this.postProcessForm(this.form)
        }
        this.isModalOpen = true
      } catch (err) {
        this.formErrors.general = err.response?.data?.error || 'Erro ao carregar os dados'
      } finally {
        this.loadingItemDetails = false
      }
    },

    closeModal() {
      this.isModalOpen = false
      this.editingItem = null
      this.form = { ...this.formFields }
      this.formErrors = {}
      this.loadItems(this.filterParams)
    },

    openSaveConfirm() {
      this.saveConfirmOpen = true
    },

    async saveItem() {
      this.saving = true
      this.formErrors = {}

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const headers = { Authorization: `Bearer ${userToken}`, 'Tenant-Authorization': `Bearer ${tenantToken}` }
        const resourceName = this.getResourceName()
        const baseEndpoint = this.apiEndpoint.split('?')[0]

        if (typeof this.beforeSave === 'function') {
          const result = await this.beforeSave(this.form)
          if (result === false) {
            this.saving = false
            return
          }
        }

        // Enviar apenas os campos definidos em formFields
        const cleanedForm = {}
        for (const key in this.formFields) {
          const value = this.form[key]
          if (value === undefined) continue

          const normalizedValue = await this.normalizeValueForPayload(key, value)
          if (normalizedValue !== undefined) {
            cleanedForm[key] = normalizedValue
          }
        }

        const useFormData = this.formNeedsMultipart(cleanedForm)
        const payload = useFormData ? this.buildFormData(resourceName, cleanedForm) : { [resourceName]: cleanedForm }
        const requestConfig = { headers }

        if (this.editingItem) {
          const updateEndpoint = this.customUpdateEndpoint 
            ? this.customUpdateEndpoint(this.editingItem.id)
            : `${baseEndpoint}/${this.editingItem.id}`
          
          if (useFormData) {
            payload.append('_method', 'PATCH')
            await axios.post(updateEndpoint, payload, requestConfig)
          } else {
            await axios.patch(updateEndpoint, payload, requestConfig)
          }
        } else {
          await axios.post(baseEndpoint, payload, requestConfig)
        }

        this.closeModal()
        this.loadItems(this.filterParams)
      } catch (err) {
        console.error('Erro ao salvar:', err)
        console.error('Response data:', err.response?.data)
        
        const errorData = err.response?.data
        let errorMessage = 'Erro ao salvar'
        
        if (errorData?.errors) {
          if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors.join(', ')
          } else if (typeof errorData.errors === 'string') {
            errorMessage = errorData.errors
          }
        } else if (errorData?.error) {
          errorMessage = errorData.error
        } else if (errorData?.message) {
          errorMessage = errorData.message
        } else if (err.message) {
          errorMessage = err.message
        }
        
        this.formErrors.general = errorMessage
      } finally {
        this.saving = false
      }
    },

    confirmSave() {
      this.saveConfirmOpen = false
      this.saveItem()
    },

    closeSaveConfirm() {
      this.saveConfirmOpen = false
    },

    async normalizeValueForPayload(key, value) {
      if (key === 'attachments' && Array.isArray(value)) {
        const fileOnly = value.filter(item => this.isFileLike(item))
        return fileOnly.length > 0 ? fileOnly : undefined
      }

      if (key === 'rate_disclosure' && value && typeof value === 'object' && !this.isFileLike(value)) {
        const lines = Array.isArray(value.lines)
          ? value.lines
            .map((l) => ({
              label: l?.label != null ? String(l.label) : '',
              daily_hint: l?.daily_hint != null ? String(l.daily_hint) : '',
              min_nights: l?.min_nights != null && l.min_nights !== '' ? String(l.min_nights) : ''
            }))
            .filter((l) => l.label.trim() || l.daily_hint.trim() || l.min_nights.trim())
          : []
        return { lines }
      }

      return value
    },

    formNeedsMultipart(cleanedForm) {
      return Object.values(cleanedForm).some(value => this.valueHasFile(value))
    },

    valueHasFile(value) {
      if (this.isFileLike(value)) return true
      if (Array.isArray(value)) {
        return value.some(item => this.isFileLike(item))
      }
      return false
    },

    isFileLike(value) {
      return (typeof File !== 'undefined' && value instanceof File) ||
        (typeof Blob !== 'undefined' && value instanceof Blob)
    },

    buildFormData(resourceName, cleanedForm) {
      const formData = new FormData()

      for (const key in cleanedForm) {
        const value = cleanedForm[key]

        if (Array.isArray(value)) {
          if (value.length === 0) {
            formData.append(`${resourceName}[${key}][]`, '')
          } else if (value.length > 0 && typeof value[0] === 'object' && !this.isFileLike(value[0])) {
            this.appendNestedFormData(formData, `${resourceName}[${key}]`, value)
          } else {
            value.forEach(item => {
              if (item === undefined || item === null) return
              formData.append(`${resourceName}[${key}][]`, item)
            })
          }
        } else if (value !== null && typeof value === 'object' && !this.isFileLike(value)) {
          this.appendNestedFormData(formData, `${resourceName}[${key}]`, value)
        } else if (value !== undefined) {
          formData.append(`${resourceName}[${key}]`, value === null ? '' : value)
        }
      }

      return formData
    },

    formDataScalarValue(v) {
      if (v === true) return '1'
      if (v === false) return '0'
      return String(v)
    },

    /** Serializa objetos/arrays aninhados para Rails (ex.: property[rate_disclosure][lines][0][label]). */
    appendNestedFormData(formData, prefix, value) {
      if (value === null || value === undefined) return
      if (this.isFileLike(value)) {
        formData.append(prefix, value)
        return
      }
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item === undefined || item === null) return
          if (typeof item === 'object' && !this.isFileLike(item)) {
            Object.entries(item).forEach(([k, v]) => {
              if (v === undefined || v === null || v === '') return
              formData.append(`${prefix}[${index}][${k}]`, this.formDataScalarValue(v))
            })
          } else {
            formData.append(`${prefix}[]`, this.formDataScalarValue(item))
          }
        })
        return
      }
      if (typeof value === 'object') {
        Object.entries(value).forEach(([k, v]) => {
          if (v === undefined) return
          if (Array.isArray(v)) {
            this.appendNestedFormData(formData, `${prefix}[${k}]`, v)
          } else if (v !== null && typeof v === 'object' && !this.isFileLike(v)) {
            this.appendNestedFormData(formData, `${prefix}[${k}]`, v)
          } else {
            const scalar = v === null || v === '' ? '' : this.formDataScalarValue(v)
            formData.append(`${prefix}[${k}]`, scalar)
          }
        })
      }
    },

    confirmDelete(item) {
      this.deleteConfirmItem = item
      this.deleteError = null
      this.deleteErrorMessage = null
      this.relatedStays = []
    },

    closeDeleteModal() {
      this.deleteConfirmItem = null
      this.deleteError = null
      this.deleteErrorMessage = null
      this.relatedStays = []
    },

    async deleteItem() {
      this.deleting = true
      this.error = null

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const baseEndpoint = this.apiEndpoint.split('?')[0]
        await axios.delete(`${baseEndpoint}/${this.deleteConfirmItem.id}`, {
          headers: { Authorization: `Bearer ${userToken}`, 'Tenant-Authorization': `Bearer ${tenantToken}` }
        })

        this.closeDeleteModal()
        this.closeModal()
        this.loadItems()
      } catch (err) {
        if (err.response?.status === 409) {
          this.deleteError = err.response.data.error
          this.deleteErrorMessage = err.response.data.message
          this.relatedStays = err.response.data.related_stays || []
        } else {
          this.error = err.response?.data?.error || 'Erro ao excluir'
          this.closeDeleteModal()
        }
      } finally {
        this.deleting = false
      }
    },

    goToStay(stayId) {
      this.closeDeleteModal()
      navigateTo('bookings', { stayId })
    },

    openDeleteRelatedStayConfirm(stay) {
      this.relatedStayToDelete = stay
    },

    async confirmDeleteRelatedStay() {
      if (!this.relatedStayToDelete) return
      await this.deleteRelatedStay(this.relatedStayToDelete.id)
      this.relatedStayToDelete = null
    },

    cancelDeleteRelatedStay() {
      this.relatedStayToDelete = null
    },

    async deleteRelatedStay(stayId) {
      if (!confirm('Tem certeza que deseja excluir esta locação?')) {
        return
      }

      this.deletingStayId = stayId

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        await axios.delete(`/api/v1/stays/${stayId}`, {
          headers: { Authorization: `Bearer ${userToken}`, 'Tenant-Authorization': `Bearer ${tenantToken}` }
        })

        // Remove a locação da lista
        this.relatedStays = this.relatedStays.filter(stay => stay.id !== stayId)

        // Se não há mais locações relacionadas, tenta excluir o item novamente
        if (this.relatedStays.length === 0) {
          this.deleteError = null
          this.deleteErrorMessage = null
          await this.deleteItem()
        }
      } catch (err) {
        this.error = err.response?.data?.error || 'Erro ao excluir locação'
        setTimeout(() => { this.error = null }, 3000)
      } finally {
        this.deletingStayId = null
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const [year, month, day] = dateString.split('-')
      const date = new Date(year, month - 1, day)
      return date.toLocaleDateString('pt-BR')
    },

    getResourceName() {
      const endpoint = this.apiEndpoint.split('?')[0]
      const lastSegment = endpoint.split('/').pop()
      if (lastSegment === 'people') {
        return 'person'
      }
      if (lastSegment === 'properties') {
        return 'property'
      }
      return lastSegment.slice(0, -1)
    }
  }
}
</script>
